import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, RefreshCw, FileText, Waves, Users, Mail, Send, ChevronDown, Calendar, FileDown, ClipboardList, LockKeyhole, ChevronLeft, Anchor, Ship, Compass, Award, Archive, UserPlus, X, Settings, User } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import CamperCard from "@/components/CamperCard";
import InlineCamperRow from "@/components/InlineCamperRow";
import CamperDetailModal from "@/components/CamperDetailModal";
import SwimmerReportModal from "@/components/SwimmerReportModal";
import DailyChangesModal from "@/components/DailyChangesModal";
import ArchiveView from "@/components/ArchiveView";
import { useAuth } from "@/contexts/AuthContext";

// Session storage key for persisting lifeguard selection
const LIFEGUARD_SESSION_KEY = "swimtrack_selected_lifeguard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_swimtrack-4/artifacts/manjzwlv_rolling%20river%20transparent.png";

// Division mapping: sub-division → main division
const DIVISION_MAP = {
  // Direct mappings (from CamperSnapshot)
  "Skippers": "Skippers",
  "Mariners": "Mariners", 
  "Navigators": "Navigators",
  "Other": "CIT",
  // Legacy Campminder sub-divisions
  "PreK Boys": "Skippers",
  "PreK Girls": "Skippers",
  "K-Girls": "Skippers",
  "K-Girls ": "Skippers",
  "K- Boys": "Skippers",
  "K- Boys ": "Skippers",
  "K-Boys": "Skippers",
  "K Boys": "Skippers",
  "1st/2nd Boys": "Mariners",
  "1st/2nd Girls": "Mariners",
  "3rd/4th Boys": "Mariners",
  "3rd/4th Girls": "Mariners",
  "Navigator Boys": "Navigators",
  "Navigator Girls": "Navigators",
  "CIT Boys": "CIT",
  "CIT Girls": "CIT",
  "5 Star": "5 Star",
};

const DIVISION_ORDER = ["Skippers", "Mariners", "Navigators", "CIT", "5 Star"];

const DIVISION_STYLE = {
  Skippers:   { bg: "bg-sky-50",    border: "border-sky-200",   accent: "text-sky-700",   iconBg: "bg-sky-100",   icon: Anchor },
  Mariners:   { bg: "bg-indigo-50", border: "border-indigo-200", accent: "text-indigo-700", iconBg: "bg-indigo-100", icon: Ship },
  Navigators: { bg: "bg-emerald-50", border: "border-emerald-200", accent: "text-emerald-700", iconBg: "bg-emerald-100", icon: Compass },
  CIT:        { bg: "bg-amber-50",  border: "border-amber-200", accent: "text-amber-700",  iconBg: "bg-amber-100",  icon: Award },
  "5 Star":   { bg: "bg-rose-50",   border: "border-rose-200",  accent: "text-rose-700",   iconBg: "bg-rose-100",   icon: Award },
};

function getMainDivision(division) {
  if (!division) return "Other";
  const trimmed = division.trim();
  return DIVISION_MAP[trimmed] || DIVISION_MAP[division] || "Other";
}

export default function Dashboard() {
  const { lock } = useAuth();
  const [campers, setCampers] = useState([]);
  const [filteredCampers, setFilteredCampers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [globalSearch, setGlobalSearch] = useState("");
  const [globalSearchResults, setGlobalSearchResults] = useState([]);
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const [colorFilter, setColorFilter] = useState(null);
  const [groupFilter, setGroupFilter] = useState("all");
  const [levelFilter, setLevelFilter] = useState("all");
  const [activeDivision, setActiveDivision] = useState(null);
  const [activeGroup, setActiveGroup] = useState(null);
  const [selectedCamper, setSelectedCamper] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showEndOfSummerModal, setShowEndOfSummerModal] = useState(false);
  const [endOfSummerPreview, setEndOfSummerPreview] = useState(null);
  const [sendingReports, setSendingReports] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [scheduleStatus, setScheduleStatus] = useState(null);
  const [showDailyChangesModal, setShowDailyChangesModal] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [emailsEnabled, setEmailsEnabled] = useState(true);
  const [showLifeguardsModal, setShowLifeguardsModal] = useState(false);
  const [lifeguards, setLifeguards] = useState([]);
  const [newLifeguardName, setNewLifeguardName] = useState("");
  const [selectedLifeguard, setSelectedLifeguard] = useState(() => {
    // Restore from session storage
    return sessionStorage.getItem(LIFEGUARD_SESSION_KEY) || "";
  });

  // Add Camper (5-Star) modal state
  const [showAddCamperModal, setShowAddCamperModal] = useState(false);
  const [newCamperFirstName, setNewCamperFirstName] = useState("");
  const [newCamperLastName, setNewCamperLastName] = useState("");
  const [newCamperDivision, setNewCamperDivision] = useState("Skippers");
  const [addingCamper, setAddingCamper] = useState(false);
  const [syncingGroupCodes, setSyncingGroupCodes] = useState(false);

  // Swim levels
  const swimLevels = ["PSA1", "PSA2", "1", "2", "3", "4", "5", "6"];

  // Load lifeguards
  const loadLifeguards = async () => {
    try {
      const resp = await axios.get(`${API}/lifeguards?include_inactive=true`);
      setLifeguards(resp.data);
    } catch (error) {
      console.error("Error loading lifeguards:", error);
    }
  };

  // Add lifeguard
  const addLifeguard = async () => {
    if (!newLifeguardName.trim()) {
      toast.error("Please enter a name");
      return;
    }
    try {
      await axios.post(`${API}/lifeguards`, { name: newLifeguardName.trim() });
      setNewLifeguardName("");
      loadLifeguards();
      toast.success("Lifeguard added");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add lifeguard");
    }
  };

  // Toggle lifeguard active status
  const toggleLifeguard = async (id, currentActive) => {
    try {
      await axios.patch(`${API}/lifeguards/${id}?active=${!currentActive}`);
      loadLifeguards();
      toast.success(currentActive ? "Lifeguard deactivated" : "Lifeguard reactivated");
    } catch (error) {
      toast.error("Failed to update lifeguard");
    }
  };

  // Handle selecting active lifeguard for inline editing
  const handleSelectActiveLifeguard = (name) => {
    setSelectedLifeguard(name);
    sessionStorage.setItem(LIFEGUARD_SESSION_KEY, name);
  };

  // Add manual camper (5-Star)
  const addManualCamper = async () => {
    if (!newCamperFirstName.trim() || !newCamperLastName.trim()) {
      toast.error("Please enter first and last name");
      return;
    }
    setAddingCamper(true);
    try {
      await axios.post(`${API}/campers`, {
        first_name: newCamperFirstName.trim(),
        last_name: newCamperLastName.trim(),
        division: "5 Star",
        bunk: "5 Star",
        group_code: "5STAR",
        swim_level: "-",
        swim_color: "-",
        source: "manual"
      });
      toast.success(`${newCamperFirstName} ${newCamperLastName} added to 5 Star!`);
      setNewCamperFirstName("");
      setNewCamperLastName("");
      // Refresh campers list
      const campersResp = await axios.get(`${API}/campers`);
      setCampers(campersResp.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add camper");
    } finally {
      setAddingCamper(false);
    }
  };

  // Sync group codes from CampMinder
  const syncGroupCodes = async () => {
    setSyncingGroupCodes(true);
    try {
      const resp = await axios.post(`${API}/sync/group-codes`);
      const data = resp.data;
      if (data.success) {
        toast.success(`Group codes synced!`, {
          description: `Matched: ${data.matched}, Updated: ${data.updated}, Unmatched: ${data.unmatched}`
        });
        // Refresh campers to see updated group codes
        const campersResp = await axios.get(`${API}/campers`);
        setCampers(campersResp.data);
      } else {
        toast.error("Sync failed", { description: data.error || "Unknown error" });
      }
    } catch (error) {
      toast.error("Sync failed", { description: error.response?.data?.detail || "Unknown error" });
    } finally {
      setSyncingGroupCodes(false);
    }
  };

  // Load email settings on mount
  useEffect(() => {
    const loadEmailSettings = async () => {
      try {
        const resp = await axios.get(`${API}/settings/emails`);
        setEmailsEnabled(resp.data.emails_enabled);
      } catch (error) {
        console.error("Error loading email settings:", error);
      }
    };
    loadEmailSettings();
    loadLifeguards();
  }, []);

  const toggleEmails = async () => {
    try {
      const newValue = !emailsEnabled;
      await axios.post(`${API}/settings/emails`, { enabled: newValue });
      setEmailsEnabled(newValue);
      toast.success(`Emails ${newValue ? "enabled" : "disabled"}`);
    } catch (error) {
      console.error("Error toggling emails:", error);
      toast.error("Failed to toggle emails");
    }
  };

  // Global search - filter campers AND groups as user types
  const [globalSearchGroups, setGlobalSearchGroups] = useState([]);
  
  useEffect(() => {
    if (globalSearch.trim().length >= 2) {
      const query = globalSearch.toLowerCase();
      
      // Search campers
      const camperResults = campers.filter(c => {
        const fullName = `${c.first_name} ${c.last_name}`.toLowerCase();
        const reverseName = `${c.last_name} ${c.first_name}`.toLowerCase();
        const bunk = (c.bunk || "").toLowerCase();
        const groupCode = (c.group_code || "").toLowerCase();
        return fullName.includes(query) || 
               reverseName.includes(query) || 
               bunk.includes(query) || 
               groupCode.includes(query);
      }).slice(0, 10);
      setGlobalSearchResults(camperResults);
      
      // Search groups - build unique groups with their info
      const groupMap = {};
      campers.forEach(c => {
        const bunk = c.bunk || "";
        const groupCode = c.group_code || "";
        const division = getMainDivision(c.division);
        if (bunk && !groupMap[bunk]) {
          groupMap[bunk] = { name: bunk, code: groupCode, division, count: 0 };
        }
        if (bunk) groupMap[bunk].count++;
      });
      
      const groupResults = Object.values(groupMap).filter(g => {
        const name = g.name.toLowerCase();
        const code = (g.code || "").toLowerCase();
        return name.includes(query) || code.includes(query);
      }).slice(0, 5);
      setGlobalSearchGroups(groupResults);
    } else {
      setGlobalSearchResults([]);
      setGlobalSearchGroups([]);
    }
  }, [globalSearch, campers]);

  // Handle clicking a camper search result
  const handleSearchResultClick = (camper) => {
    setSelectedCamper(camper);
    setGlobalSearch("");
    setGlobalSearchResults([]);
    setGlobalSearchGroups([]);
    setShowGlobalSearch(false);
  };

  // Handle clicking a group search result - navigate to that group
  const handleGroupSearchClick = (group) => {
    setActiveDivision(group.division);
    setActiveGroup(group.name);
    setGlobalSearch("");
    setGlobalSearchResults([]);
    setGlobalSearchGroups([]);
    setShowGlobalSearch(false);
  };

  // Get unique groups/bunks from campers (filtered by active division)
  const uniqueGroups = useMemo(() => {
    const source = activeDivision
      ? campers.filter(c => getMainDivision(c.division) === activeDivision)
      : campers;
    const groups = source
      .map(c => c.bunk)
      .filter(Boolean)
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort();
    return groups;
  }, [campers, activeDivision]);

  const fetchCampers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/campers`);
      setCampers(response.data);
      setFilteredCampers(response.data);
    } catch (error) {
      console.error("Error fetching campers:", error);
      toast.error("Failed to load campers");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSyncStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sync/status`);
      setSyncStatus(response.data);
    } catch (error) {
      console.error("Error fetching sync status:", error);
    }
  }, []);

  const seedDemoData = async () => {
    try {
      await axios.post(`${API}/seed-demo-data`);
      toast.success("Demo data loaded successfully");
      fetchCampers();
    } catch (error) {
      console.error("Error seeding demo data:", error);
      toast.error("Failed to load demo data");
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      await axios.post(`${API}/sync/campers`);
      toast.success("Full sync started - this may take a few minutes");
      // Poll for completion
      const pollInterval = setInterval(async () => {
        const statusResponse = await axios.get(`${API}/sync/status`);
        if (!statusResponse.data.is_syncing) {
          clearInterval(pollInterval);
          fetchCampers();
          fetchSyncStatus();
          setSyncing(false);
          toast.success(`Sync complete: ${statusResponse.data.campers_synced} campers`);
        }
      }, 5000);
    } catch (error) {
      console.error("Error syncing:", error);
      toast.error("Failed to sync with Campminder");
      setSyncing(false);
    }
  };

  const handleQuickSync = async () => {
    try {
      setSyncing(true);
      await axios.post(`${API}/sync/quick`);
      toast.success("Quick sync started - checking for swim color changes");
      // Poll for completion
      const pollInterval = setInterval(async () => {
        const statusResponse = await axios.get(`${API}/sync/status`);
        if (!statusResponse.data.is_syncing) {
          clearInterval(pollInterval);
          fetchCampers();
          fetchSyncStatus();
          setSyncing(false);
          toast.success(statusResponse.data.message || "Quick sync complete");
        }
      }, 2000);
    } catch (error) {
      console.error("Error quick syncing:", error);
      toast.error("Failed to quick sync");
      setSyncing(false);
    }
  };

  const [pushing, setPushing] = useState(false);
  
  const handlePushColors = async () => {
    try {
      setPushing(true);
      
      // First check if config is set
      const debugResp = await axios.get(`${API}/sync/push-debug`);
      const config = debugResp.data;
      
      // Show config status in alert so it's impossible to miss
      alert(`CamperSnapshot Config:\n\nAPI URL: ${config.CAMPERSNAPSHOT_API_URL}\nAPI KEY: ${config.CAMPERSNAPSHOT_API_KEY}\nROSTER URL: ${config.CAMPERSNAPSHOT_ROSTER_URL}`);
      
      if (config.CAMPERSNAPSHOT_API_URL === "NOT SET" || config.CAMPERSNAPSHOT_API_KEY === "NOT SET") {
        toast.error("CamperSnapshot NOT configured!");
        setPushing(false);
        return;
      }
      
      const resp = await axios.post(`${API}/sync/push-colors`);
      toast.success("Push started: " + JSON.stringify(resp.data));
      
      setTimeout(() => {
        setPushing(false);
        toast.success("Push complete!");
      }, 5000);
    } catch (error) {
      console.error("Error pushing colors:", error);
      alert("Push ERROR: " + (error.response?.data?.detail || error.message));
      toast.error("Failed to push: " + (error.response?.data?.detail || error.message));
      setPushing(false);
    }
  };

  const fetchEndOfSummerPreview = async () => {
    try {
      const response = await axios.get(`${API}/end-of-summer/preview`);
      setEndOfSummerPreview(response.data);
      // Also fetch schedule status
      const scheduleResponse = await axios.get(`${API}/end-of-summer/schedule`);
      setScheduleStatus(scheduleResponse.data);
    } catch (error) {
      console.error("Error fetching preview:", error);
      toast.error("Failed to load preview");
    }
  };

  const handleOpenEndOfSummerModal = () => {
    fetchEndOfSummerPreview();
    setShowEndOfSummerModal(true);
  };

  const handleSendAllReports = async () => {
    if (!endOfSummerPreview || endOfSummerPreview.reports_to_send === 0) {
      toast.error("No campers have parent emails configured");
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to send ${endOfSummerPreview.reports_to_send} end of summer reports to parents?\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      setSendingReports(true);
      const response = await axios.post(`${API}/end-of-summer/send-all`);
      toast.success(response.data.message || "Reports are being sent!");
      setShowEndOfSummerModal(false);
    } catch (error) {
      console.error("Error sending reports:", error);
      toast.error(error.response?.data?.detail || "Failed to send reports");
    } finally {
      setSendingReports(false);
    }
  };

  useEffect(() => {
    fetchCampers();
    fetchSyncStatus();
  }, [fetchCampers, fetchSyncStatus]);

  useEffect(() => {
    let result = campers;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.first_name?.toLowerCase().includes(query) ||
          c.last_name?.toLowerCase().includes(query) ||
          c.full_name?.toLowerCase().includes(query) ||
          c.bunk?.toLowerCase().includes(query) ||  // Search by group/bunk name
          c.group_code?.toLowerCase().includes(query) ||  // Search by group code/number
          c.division?.toLowerCase().includes(query)  // Search by division
      );
    }

    // Apply group filter
    if (groupFilter && groupFilter !== "all") {
      result = result.filter((c) => c.bunk === groupFilter);
    }

    // Apply division filter (when viewing a specific division)
    if (activeDivision) {
      result = result.filter((c) => getMainDivision(c.division) === activeDivision);
    }

    // Apply group filter (when viewing a specific group)
    if (activeGroup) {
      result = result.filter((c) => (c.bunk || "Unassigned") === activeGroup);
    }

    // Apply swim level filter
    if (levelFilter && levelFilter !== "all") {
      if (levelFilter === "-") {
        result = result.filter((c) => !c.swim_level || c.swim_level === "-");
      } else {
        result = result.filter((c) => c.swim_level === levelFilter);
      }
    }

    if (colorFilter) {
      if (colorFilter === "-") {
        // Filter for "Not Set"
        result = result.filter((c) => c.swim_color === "-" || !c.swim_color);
      } else if (colorFilter.includes("/")) {
        // Exact match for combo colors like "Red / Blue"
        result = result.filter((c) => c.swim_color === colorFilter);
      } else {
        // Filter by single color - exact match only (Yellow, Red, Blue, Green)
        result = result.filter((c) => 
          c.swim_color && c.swim_color.toLowerCase() === colorFilter.toLowerCase()
        );
      }
    }

    setFilteredCampers(result);
  }, [searchQuery, colorFilter, groupFilter, levelFilter, activeDivision, activeGroup, campers]);

  const handleCamperUpdate = (updatedCamper) => {
    // Update the campers list
    setCampers((prev) =>
      prev.map((c) => (c.id === updatedCamper.id ? updatedCamper : c))
    );
    // Also update the selected camper if it's the same one
    if (selectedCamper && selectedCamper.id === updatedCamper.id) {
      setSelectedCamper(updatedCamper);
    }
  };

  // Helper to check if a swim color matches exactly or contains a color
  const hasColor = (swimColor, baseColor) => {
    if (!swimColor || swimColor === "-") return false;
    return swimColor.toLowerCase().includes(baseColor.toLowerCase());
  };

  // Helper to check exact match for combo colors
  const isExactColor = (swimColor, targetColor) => {
    if (!swimColor || swimColor === "-") return false;
    return swimColor.toLowerCase().replace(/\s+/g, '') === targetColor.toLowerCase().replace(/\s+/g, '');
  };

  // Get unique swim colors from campers for combo detection
  const uniqueColors = [...new Set(campers.map(c => c.swim_color).filter(c => c && c !== "-"))];

  // Division counts for filter tabs
  const divisionCounts = useMemo(() => {
    const counts = {};
    DIVISION_ORDER.forEach(d => { counts[d] = 0; });
    campers.forEach(c => {
      const main = getMainDivision(c.division);
      if (counts[main] !== undefined) counts[main]++;
    });
    return counts;
  }, [campers]);

  // Per-division color breakdowns for division cards
  const divisionColorStats = useMemo(() => {
    const stats = {};
    DIVISION_ORDER.forEach(d => {
      stats[d] = { Green: 0, Yellow: 0, Red: 0, Blue: 0, NotSet: 0 };
    });
    campers.forEach(c => {
      const main = getMainDivision(c.division);
      if (!stats[main]) return;
      const color = (c.swim_color || "").toLowerCase();
      if (!color || color === "-") stats[main].NotSet++;
      else if (color === "green") stats[main].Green++;
      else if (color === "yellow") stats[main].Yellow++;
      else if (color === "red") stats[main].Red++;
      else if (color === "blue") stats[main].Blue++;
      else if (color.includes("green")) stats[main].Green++;
      else if (color.includes("yellow")) stats[main].Yellow++;
      else if (color.includes("red")) stats[main].Red++;
      else if (color.includes("blue")) stats[main].Blue++;
      else stats[main].NotSet++;
    });
    return stats;
  }, [campers]);

  // Groups within the active division (for group cards view)
  const divisionGroups = useMemo(() => {
    if (!activeDivision) return [];
    const divCampers = campers.filter(c => getMainDivision(c.division) === activeDivision);
    const groupMap = {};
    divCampers.forEach(c => {
      const g = c.bunk || "Unassigned";
      if (!groupMap[g]) {
        groupMap[g] = { 
          name: g, 
          groupCode: c.group_code || null, // Store group code/number
          campers: [], 
          colors: { Green: 0, Yellow: 0, Red: 0, Blue: 0 } 
        };
      }
      groupMap[g].campers.push(c);
      // Update group code if not set (take first non-null)
      if (!groupMap[g].groupCode && c.group_code) {
        groupMap[g].groupCode = c.group_code;
      }
      const color = (c.swim_color || "").toLowerCase();
      if (color === "green") groupMap[g].colors.Green++;
      else if (color === "yellow") groupMap[g].colors.Yellow++;
      else if (color === "red") groupMap[g].colors.Red++;
      else if (color === "blue") groupMap[g].colors.Blue++;
      else if (color.includes("green")) groupMap[g].colors.Green++;
      else if (color.includes("yellow")) groupMap[g].colors.Yellow++;
      else if (color.includes("red")) groupMap[g].colors.Red++;
      else if (color.includes("blue")) groupMap[g].colors.Blue++;
    });
    return Object.values(groupMap).sort((a, b) => a.name.localeCompare(b.name));
  }, [campers, activeDivision]);

  // Group filtered campers by main division in order
  const groupedCampers = useMemo(() => {
    const groups = {};
    DIVISION_ORDER.forEach(d => { groups[d] = []; });
    groups["Other"] = [];

    filteredCampers.forEach(c => {
      const main = getMainDivision(c.division);
      if (groups[main]) groups[main].push(c);
      else groups["Other"].push(c);
    });

    // Sort within each group by last name then first name
    Object.values(groups).forEach(arr =>
      arr.sort((a, b) => (a.last_name || "").localeCompare(b.last_name || "") || (a.first_name || "").localeCompare(b.first_name || ""))
    );

    return DIVISION_ORDER
      .map(d => ({ division: d, campers: groups[d] }))
      .concat(groups["Other"].length ? [{ division: "Other", campers: groups["Other"] }] : [])
      .filter(g => g.campers.length > 0);
  }, [filteredCampers]);
  
  // Identify combo colors (contains "/" or multiple color words)
  const comboColors = uniqueColors.filter(color => 
    color.includes("/") || 
    (color.toLowerCase().match(/yellow|red|blue|green/g) || []).length > 1
  );

  const colorCounts = {
    Yellow: campers.filter((c) => isExactColor(c.swim_color, "Yellow")).length,
    Red: campers.filter((c) => isExactColor(c.swim_color, "Red")).length,
    Blue: campers.filter((c) => isExactColor(c.swim_color, "Blue")).length,
    Green: campers.filter((c) => isExactColor(c.swim_color, "Green")).length,
    NotSet: campers.filter((c) => c.swim_color === "-" || !c.swim_color).length,
  };

  // Count combo colors dynamically
  const comboCounts = {};
  comboColors.forEach(combo => {
    comboCounts[combo] = campers.filter(c => c.swim_color === combo).length;
  });

  // Handle clicking on a stat badge to filter
  const handleStatClick = (color) => {
    if (colorFilter === color) {
      setColorFilter(null); // Toggle off if already selected
    } else {
      setColorFilter(color);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Light & Mobile Optimized */}
      <header className="bg-white shadow-sm sticky top-0 z-40 px-3 py-2 md:px-6 md:py-3" data-testid="app-header">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img
              src={LOGO_URL}
              alt="Rolling River Day Camp"
              className="h-8 md:h-10 w-auto"
              data-testid="logo"
            />
            <div className="hidden sm:block">
              <h1 className="text-base md:text-lg font-bold text-gray-800 font-['Manrope']">
                Swim Track
              </h1>
              <p className="text-xs text-gray-500">2026 Season</p>
            </div>
          </div>

          {/* Mobile: Icon-only buttons */}
          <div className="flex items-center gap-1 md:gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleQuickSync}
              disabled={syncing}
              className="h-8 w-8 md:w-auto md:px-3 p-0 border-cyan-500 text-cyan-600 hover:bg-cyan-50"
              data-testid="quick-sync-btn"
              title="Quick sync"
            >
              <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
              <span className="hidden md:inline ml-2">Quick</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSync}
              disabled={syncing}
              className="h-8 w-8 md:w-auto md:px-3 p-0 border-gray-300 text-gray-600 hover:bg-gray-100"
              data-testid="sync-btn"
              title="Full sync"
            >
              <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
              <span className="hidden md:inline ml-2">Full</span>
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handlePushColors}
              disabled={pushing}
              className="h-8 w-8 md:w-auto md:px-3 p-0 bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="push-colors-btn"
              title="Push all colors to CamperSnapshot"
            >
              <Send className={`h-4 w-4 ${pushing ? "animate-pulse" : ""}`} />
              <span className="hidden md:inline ml-2">Push</span>
            </Button>
            <Button
              variant={emailsEnabled ? "default" : "outline"}
              size="sm"
              onClick={toggleEmails}
              className={`h-8 w-8 md:w-auto md:px-3 p-0 ${emailsEnabled ? "bg-green-500 hover:bg-green-600 text-white" : "bg-gray-200 text-gray-500 border-gray-300"}`}
              data-testid="email-toggle-btn"
              title={emailsEnabled ? "Emails ON - Click to disable" : "Emails OFF - Click to enable"}
            >
              <Mail className="h-4 w-4" />
              <span className="hidden md:inline ml-2">{emailsEnabled ? "ON" : "OFF"}</span>
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => setShowDailyChangesModal(true)}
              className="h-8 w-8 md:w-auto md:px-3 p-0 bg-purple-500 hover:bg-purple-600 text-white"
              data-testid="daily-changes-btn"
              title="Daily Report"
            >
              <ClipboardList className="h-4 w-4" />
              <span className="hidden md:inline ml-2">Daily</span>
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => setShowReportModal(true)}
              className="h-8 w-8 md:w-auto md:px-3 p-0 bg-blue-500 hover:bg-blue-600 text-white"
              data-testid="report-btn"
              title="Swimmer Report"
            >
              <FileText className="h-4 w-4" />
              <span className="hidden md:inline ml-2">Report</span>
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleOpenEndOfSummerModal}
              className="h-8 w-8 md:w-auto md:px-3 p-0 bg-emerald-500 hover:bg-emerald-600 text-white hidden sm:flex"
              data-testid="end-of-summer-btn"
              title="Email Parents"
            >
              <Mail className="h-4 w-4" />
              <span className="hidden md:inline ml-2">Email</span>
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => window.open(`${BACKEND_URL}/api/swim-flyer`, '_blank')}
              className="h-8 w-8 md:w-auto md:px-3 p-0 bg-amber-500 hover:bg-amber-600 text-white hidden sm:flex"
              data-testid="parent-flyer-btn"
              title="Parent Flyer"
            >
              <FileDown className="h-4 w-4" />
              <span className="hidden md:inline ml-2">Flyer</span>
            </Button>
            <div className="h-5 w-px bg-gray-300 flex-shrink-0 hidden sm:block" />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAddCamperModal(true)}
              className="h-8 w-8 p-0 text-amber-500 hover:text-amber-700 hover:bg-amber-50"
              data-testid="add-camper-btn"
              title="Add 5-Star Camper"
            >
              <UserPlus className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={syncGroupCodes}
              disabled={syncingGroupCodes}
              className="h-8 w-8 p-0 text-blue-500 hover:text-blue-700 hover:bg-blue-50"
              data-testid="sync-group-codes-btn"
              title="Sync Group Numbers from CampMinder"
            >
              {syncingGroupCodes ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <ClipboardList className="h-4 w-4" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowArchive(true)}
              className="h-8 w-8 p-0 text-gray-400 hover:text-gray-700 hover:bg-gray-100"
              data-testid="archive-btn"
              title="Season Archives"
            >
              <Archive className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowLifeguardsModal(true)}
              className="h-8 w-8 p-0 text-gray-400 hover:text-blue-500 hover:bg-blue-50"
              data-testid="lifeguards-btn"
              title="Manage Lifeguards"
            >
              <Settings className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={lock}
              className="h-8 w-8 p-0 text-gray-400 hover:text-red-500 hover:bg-red-50"
              data-testid="lock-btn"
              title="Lock session"
            >
              <LockKeyhole className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Lifeguards Management Modal */}
      <Dialog open={showLifeguardsModal} onOpenChange={setShowLifeguardsModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Lifeguards</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Add new lifeguard */}
            <div className="flex gap-2">
              <Input
                placeholder="New lifeguard name"
                value={newLifeguardName}
                onChange={(e) => setNewLifeguardName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addLifeguard()}
                className="flex-1"
              />
              <Button onClick={addLifeguard} size="sm">
                <UserPlus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
            
            {/* Lifeguards list */}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {lifeguards.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No lifeguards added yet</p>
              ) : (
                lifeguards.map((lg) => (
                  <div 
                    key={lg.id} 
                    className={`flex items-center justify-between p-2 rounded-lg ${lg.active ? "bg-gray-50" : "bg-gray-100 opacity-60"}`}
                  >
                    <span className={lg.active ? "text-gray-800" : "text-gray-400 line-through"}>
                      {lg.name}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleLifeguard(lg.id, lg.active)}
                      className={lg.active ? "text-red-500 hover:text-red-700" : "text-green-500 hover:text-green-700"}
                    >
                      {lg.active ? <X className="h-4 w-4" /> : "Reactivate"}
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add 5-Star Camper Modal */}
      <Dialog open={showAddCamperModal} onOpenChange={setShowAddCamperModal}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800 flex items-center gap-2">
              <Award className="h-5 w-5 text-amber-500" />
              Add 5 Star Camper
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Add campers manually to the "5 Star" group. They will function identically to synced campers.
            </p>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">First Name</label>
                <Input
                  value={newCamperFirstName}
                  onChange={(e) => setNewCamperFirstName(e.target.value)}
                  placeholder="First name"
                  className="bg-gray-50 border-gray-300"
                  data-testid="add-camper-firstname"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Last Name</label>
                <Input
                  value={newCamperLastName}
                  onChange={(e) => setNewCamperLastName(e.target.value)}
                  placeholder="Last name"
                  className="bg-gray-50 border-gray-300"
                  data-testid="add-camper-lastname"
                />
              </div>
            </div>

            <p className="text-sm text-gray-500">
              Campers added here will appear in the <strong>5 Star</strong> division.
            </p>

            <div className="flex gap-2 pt-2">
              <Button
                onClick={addManualCamper}
                disabled={addingCamper || !newCamperFirstName.trim() || !newCamperLastName.trim()}
                className="flex-1 bg-rose-500 hover:bg-rose-600 text-white"
                data-testid="add-camper-submit"
              >
                {addingCamper ? "Adding..." : "Add to 5 Star"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowAddCamperModal(false)}
                className="border-gray-300"
              >
                Done
              </Button>
            </div>

            <p className="text-xs text-gray-400 text-center">
              After adding, form stays ready for the next camper
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      {showArchive ? (
        <ArchiveView onBack={() => setShowArchive(false)} />
      ) : (
      <main className="max-w-7xl mx-auto px-3 py-4 md:px-6" data-testid="main-content">

        {!activeDivision ? (
          /* ===== DIVISION CARDS VIEW ===== */
          <>
            {/* Stats Bar - Compact for mobile */}
            <div className="flex items-center gap-2 md:gap-4 mb-4 overflow-x-auto pb-2 -mx-3 px-3">
              <div className="flex items-center gap-1 text-gray-600 flex-shrink-0">
                <Users className="h-4 w-4" />
                <span className="font-bold text-gray-800">{campers.length}</span>
                <span className="text-xs">Campers</span>
              </div>
              <div className="h-5 w-px bg-gray-300 flex-shrink-0" />
          <div className="flex items-center gap-1 md:gap-2 flex-nowrap">
            {colorCounts.NotSet > 0 && (
              <div 
                className={`flex flex-col items-center justify-center bg-gray-100 text-gray-600 border border-gray-300 cursor-pointer hover:bg-gray-200 transition-colors rounded-md px-2 py-1 min-w-[32px] ${colorFilter === "-" ? "ring-2 ring-blue-500" : ""}`}
                onClick={() => handleStatClick("-")}
                data-testid="notset-count"
              >
                <span className="text-sm font-bold leading-none">{colorCounts.NotSet}</span>
                <span className="text-[10px] leading-none mt-0.5">—</span>
              </div>
            )}
            {colorCounts.Yellow > 0 && (
              <div 
                className={`flex flex-col items-center justify-center bg-yellow-100 text-yellow-700 border border-yellow-400 cursor-pointer hover:bg-yellow-200 transition-colors rounded-md px-2 py-1 min-w-[32px] ${colorFilter === "Yellow" ? "ring-2 ring-blue-500" : ""}`}
                onClick={() => handleStatClick("Yellow")}
                data-testid="yellow-count"
              >
                <span className="text-sm font-bold leading-none">{colorCounts.Yellow}</span>
                <span className="text-[10px] leading-none mt-0.5">Y</span>
              </div>
            )}
            {colorCounts.Red > 0 && (
              <div 
                className={`flex flex-col items-center justify-center bg-red-100 text-red-700 border border-red-400 cursor-pointer hover:bg-red-200 transition-colors rounded-md px-2 py-1 min-w-[32px] ${colorFilter === "Red" ? "ring-2 ring-blue-500" : ""}`}
                onClick={() => handleStatClick("Red")}
                data-testid="red-count"
              >
                <span className="text-sm font-bold leading-none">{colorCounts.Red}</span>
                <span className="text-[10px] leading-none mt-0.5">R</span>
              </div>
            )}
            {colorCounts.Blue > 0 && (
              <div 
                className={`flex flex-col items-center justify-center bg-blue-100 text-blue-700 border border-blue-400 cursor-pointer hover:bg-blue-200 transition-colors rounded-md px-2 py-1 min-w-[32px] ${colorFilter === "Blue" ? "ring-2 ring-blue-500" : ""}`}
                onClick={() => handleStatClick("Blue")}
                data-testid="blue-count"
              >
                <span className="text-sm font-bold leading-none">{colorCounts.Blue}</span>
                <span className="text-[10px] leading-none mt-0.5">B</span>
              </div>
            )}
            {colorCounts.Green > 0 && (
              <div 
                className={`flex flex-col items-center justify-center bg-green-100 text-green-700 border border-green-400 cursor-pointer hover:bg-green-200 transition-colors rounded-md px-2 py-1 min-w-[32px] ${colorFilter === "Green" ? "ring-2 ring-blue-500" : ""}`}
                onClick={() => handleStatClick("Green")}
                data-testid="green-count"
              >
                <span className="text-sm font-bold leading-none">{colorCounts.Green}</span>
                <span className="text-[10px] leading-none mt-0.5">G</span>
              </div>
            )}
            {/* Combo colors */}
            {Object.entries(comboCounts).map(([combo, count]) => {
              if (count === 0) return null;
              let bgClass = "bg-gray-100 text-gray-600 border-gray-300";
              let shortName = combo;
              const comboLower = combo.toLowerCase();
              if (comboLower.includes("yellow") && comboLower.includes("blue")) {
                bgClass = "bg-gradient-to-r from-yellow-100 to-blue-100 text-yellow-700 border-yellow-400";
                shortName = "Y/B";
              } else if (comboLower.includes("red") && comboLower.includes("blue")) {
                bgClass = "bg-gradient-to-r from-red-100 to-blue-100 text-red-700 border-red-400";
                shortName = "R/B";
              } else if (comboLower.includes("green") && comboLower.includes("blue")) {
                bgClass = "bg-gradient-to-r from-green-100 to-blue-100 text-green-700 border-green-400";
                shortName = "G/B";
              }
              return (
                <div 
                  key={combo}
                  className={`flex flex-col items-center justify-center ${bgClass} border cursor-pointer hover:opacity-80 transition-opacity rounded-md px-2 py-1 min-w-[32px] ${colorFilter === combo ? "ring-2 ring-blue-500" : ""}`}
                  onClick={() => handleStatClick(combo)}
                  data-testid={`combo-count-${combo.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <span className="text-sm font-bold leading-none">{count}</span>
                  <span className="text-[10px] leading-none mt-0.5">{shortName}</span>
                </div>
              );
            })}
          </div>
        </div>

            {/* Global Search Bar */}
            <div className="relative mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by name, group code, or group..."
                  value={globalSearch}
                  onChange={(e) => setGlobalSearch(e.target.value)}
                  onFocus={() => setShowGlobalSearch(true)}
                  className="w-full pl-9 h-11 bg-white border-gray-300 text-gray-800 placeholder:text-gray-400 focus:border-green-500 focus:ring-green-500 rounded-xl shadow-sm"
                  data-testid="global-search-input"
                />
                {globalSearch && (
                  <button 
                    onClick={() => { setGlobalSearch(""); setGlobalSearchResults([]); setGlobalSearchGroups([]); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                )}
              </div>
              
              {/* Search Results Dropdown - Groups AND Campers */}
              {(globalSearchGroups.length > 0 || globalSearchResults.length > 0) && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-96 overflow-y-auto">
                  {/* Group Results Section */}
                  {globalSearchGroups.length > 0 && (
                    <>
                      <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">
                        Groups
                      </div>
                      {globalSearchGroups.map((group) => (
                        <button
                          key={`group-${group.name}`}
                          onClick={() => handleGroupSearchClick(group)}
                          className="w-full px-4 py-3 text-left hover:bg-blue-50 flex items-center justify-between border-b border-gray-100"
                          data-testid={`search-group-${group.name}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                              <Users className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                              <div className="font-semibold text-gray-800">
                                {group.code && `${group.code} `}{group.name}
                              </div>
                              <div className="text-xs text-gray-500">{group.division}</div>
                            </div>
                          </div>
                          <span className="px-2 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700">
                            {group.count} campers
                          </span>
                        </button>
                      ))}
                    </>
                  )}
                  
                  {/* Camper Results Section */}
                  {globalSearchResults.length > 0 && (
                    <>
                      <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">
                        Campers
                      </div>
                      {globalSearchResults.map((camper) => (
                        <button
                          key={camper.id}
                          onClick={() => handleSearchResultClick(camper)}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center justify-between border-b border-gray-100 last:border-b-0"
                          data-testid={`search-result-${camper.id}`}
                        >
                          <div>
                            <div className="font-medium text-gray-800">{camper.first_name} {camper.last_name}</div>
                            <div className="text-xs text-gray-500">
                              {camper.group_code && `${camper.group_code} `}{camper.bunk} • {getMainDivision(camper.division)}
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                            camper.swim_color === "Green" ? "bg-green-100 text-green-700" :
                            camper.swim_color === "Yellow" ? "bg-yellow-100 text-yellow-700" :
                            camper.swim_color === "Red" ? "bg-red-100 text-red-700" :
                            camper.swim_color === "Blue" ? "bg-blue-100 text-blue-700" :
                            "bg-gray-100 text-gray-600"
                          }`}>
                            {camper.swim_color || "—"}
                          </span>
                        </button>
                      ))}
                    </>
                  )}
                </div>
              )}
              
              {/* No results message */}
              {globalSearch.length >= 2 && globalSearchResults.length === 0 && globalSearchGroups.length === 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-4 text-center text-gray-500">
                  No results found for "{globalSearch}"
                </div>
              )}
            </div>

            {/* Division Cards */}
            {loading ? (
              <div className="grid grid-cols-2 gap-3" data-testid="loading-skeleton">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
                    <div className="h-6 w-2/3 mb-3 bg-gray-200 rounded animate-pulse" />
                    <div className="h-8 w-1/3 mb-2 bg-gray-200 rounded animate-pulse" />
                    <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3" data-testid="division-cards">
                {DIVISION_ORDER.map((div) => {
                  const style = DIVISION_STYLE[div];
                  const Icon = style.icon;
                  const count = divisionCounts[div] || 0;
                  const colors = divisionColorStats[div] || {};
                  return (
                    <button
                      key={div}
                      onClick={() => setActiveDivision(div)}
                      className={`${style.bg} ${style.border} border rounded-xl p-4 text-left transition-all active:scale-[0.97] hover:shadow-md`}
                      data-testid={`division-card-${div.toLowerCase()}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`${style.iconBg} rounded-lg p-1.5`}>
                          <Icon className={`h-4 w-4 ${style.accent}`} />
                        </div>
                        <h3 className={`font-bold text-sm ${style.accent}`}>{div}</h3>
                      </div>
                      <div className="text-2xl font-bold text-gray-800 mb-2">{count}</div>
                      <div className="flex gap-1">
                        {colors.Green > 0 && <span className="bg-green-200 text-green-800 text-[10px] font-bold px-1.5 py-0.5 rounded">{colors.Green}G</span>}
                        {colors.Yellow > 0 && <span className="bg-yellow-200 text-yellow-800 text-[10px] font-bold px-1.5 py-0.5 rounded">{colors.Yellow}Y</span>}
                        {colors.Red > 0 && <span className="bg-red-200 text-red-800 text-[10px] font-bold px-1.5 py-0.5 rounded">{colors.Red}R</span>}
                        {colors.Blue > 0 && <span className="bg-blue-200 text-blue-800 text-[10px] font-bold px-1.5 py-0.5 rounded">{colors.Blue}B</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Sync Status */}
            {syncStatus && (
              <div className="mt-6 text-center text-xs text-gray-500" data-testid="sync-status">
                {syncStatus.last_sync
                  ? `Last synced: ${new Date(syncStatus.last_sync).toLocaleString('en-US', { timeZone: 'America/New_York' })}`
                  : "Not synced"}
              </div>
            )}
          </>
        ) : !activeGroup ? (
          /* ===== GROUP CARDS VIEW (inside a division) ===== */
          <>
            {/* Back button + Division header */}
            <div className="flex items-center gap-3 mb-4">
              <button
                onClick={() => { setActiveDivision(null); setSearchQuery(""); setColorFilter(null); }}
                className="flex items-center gap-1 text-gray-500 hover:text-gray-800 transition-colors"
                data-testid="back-to-divisions"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <div className="flex items-center gap-2">
                {(() => { const s = DIVISION_STYLE[activeDivision]; const Icon = s?.icon || Waves; return (
                  <div className={`${s?.iconBg || "bg-gray-100"} rounded-lg p-1.5`}>
                    <Icon className={`h-5 w-5 ${s?.accent || "text-gray-600"}`} />
                  </div>
                ); })()}
                <div>
                  <h2 className={`text-lg font-bold ${DIVISION_STYLE[activeDivision]?.accent || "text-gray-800"}`} data-testid="active-division-title">
                    {activeDivision}
                  </h2>
                  <span className="text-xs text-gray-500">{divisionCounts[activeDivision] || 0} campers</span>
                </div>
              </div>
            </div>

            {/* Group Cards Grid */}
            <div className="grid grid-cols-2 gap-3" data-testid="group-cards">
              {divisionGroups.map((group) => {
                const s = DIVISION_STYLE[activeDivision] || {};
                // Format as "G04 Angelfish" if group has groupCode
                const displayName = group.groupCode && group.groupCode !== group.name 
                  ? `${group.groupCode} ${group.name}` 
                  : group.name;
                return (
                  <button
                    key={group.name}
                    onClick={() => setActiveGroup(group.name)}
                    className={`${s.bg || "bg-gray-50"} ${s.border || "border-gray-200"} border rounded-xl p-4 text-left transition-all active:scale-[0.97] hover:shadow-md`}
                    data-testid={`group-card-${group.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <h3 className="font-bold text-sm text-gray-800 truncate mb-1">{displayName}</h3>
                    <div className="text-2xl font-bold text-gray-800 mb-2">{group.campers.length}</div>
                    <div className="flex gap-1 flex-wrap">
                      {group.colors.Green > 0 && <span className="bg-green-200 text-green-800 text-[10px] font-bold px-1.5 py-0.5 rounded">{group.colors.Green}G</span>}
                      {group.colors.Yellow > 0 && <span className="bg-yellow-200 text-yellow-800 text-[10px] font-bold px-1.5 py-0.5 rounded">{group.colors.Yellow}Y</span>}
                      {group.colors.Red > 0 && <span className="bg-red-200 text-red-800 text-[10px] font-bold px-1.5 py-0.5 rounded">{group.colors.Red}R</span>}
                      {group.colors.Blue > 0 && <span className="bg-blue-200 text-blue-800 text-[10px] font-bold px-1.5 py-0.5 rounded">{group.colors.Blue}B</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          /* ===== CAMPER LIST VIEW (inside a group) ===== */
          <>
            {/* Back button + Group header */}
            <div className="flex items-center gap-3 mb-4">
              <button
                onClick={() => { setActiveGroup(null); setSearchQuery(""); setColorFilter(null); setLevelFilter("all"); }}
                className="flex items-center gap-1 text-gray-500 hover:text-gray-800 transition-colors"
                data-testid="back-to-groups"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold text-gray-800" data-testid="active-group-title">{activeGroup}</h2>
                </div>
                <span className="text-xs text-gray-500">{activeDivision} &middot; {filteredCampers.length} campers</span>
              </div>
            </div>

            {/* Lifeguard Selector for inline editing */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4">
              <div className="flex items-center gap-3">
                <User className="h-5 w-5 text-blue-600 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs text-blue-700 font-medium mb-1">Active Lifeguard</p>
                  <Select
                    value={selectedLifeguard}
                    onValueChange={handleSelectActiveLifeguard}
                  >
                    <SelectTrigger 
                      className={`h-9 bg-white border-blue-200 ${!selectedLifeguard ? "text-blue-400" : "text-gray-800"}`}
                      data-testid="group-lifeguard-select"
                    >
                      <SelectValue placeholder="Select lifeguard to enable quick color saves" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {lifeguards.filter(lg => lg.active).map((lg) => (
                        <SelectItem key={lg.id} value={lg.name} className="hover:bg-gray-100">
                          {lg.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {selectedLifeguard && (
                <p className="text-xs text-blue-600 mt-2 ml-8">
                  {selectedLifeguard} selected — use dropdowns below to quickly assign colors & levels
                </p>
              )}
            </div>

            {/* Search and Filters */}
            <div className="flex flex-col gap-3 mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by name or group..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 h-10 bg-white border-gray-300 text-gray-800 placeholder:text-gray-400 focus:border-blue-500 focus:ring-blue-500"
                  data-testid="search-input"
                />
              </div>
              <div className="flex gap-2">
                <Select value={levelFilter} onValueChange={setLevelFilter}>
                  <SelectTrigger className="w-[140px] h-10 bg-white border-gray-300 text-gray-800" data-testid="level-filter-dropdown">
                    <SelectValue placeholder="Level" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 text-gray-800">
                    <SelectItem value="all" className="hover:bg-gray-100">All Levels</SelectItem>
                    <SelectItem value="-" className="hover:bg-gray-100">No Level</SelectItem>
                    {swimLevels.map((level) => (
                      <SelectItem key={level} value={level} className="hover:bg-gray-100">
                        {level.startsWith("PSA") ? `PSA ${level.replace("PSA", "")}` : `Level ${level}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Color Filter Buttons */}
              <div className="flex gap-1 flex-wrap">
                <button className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${!colorFilter ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-600 hover:bg-gray-300"}`} onClick={() => setColorFilter(null)} data-testid="filter-all">All</button>
                <button className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${colorFilter === "-" ? "bg-gray-500 text-white" : "bg-gray-200 text-gray-600 hover:bg-gray-300"}`} onClick={() => setColorFilter(colorFilter === "-" ? null : "-")} data-testid="filter-notset">Not Set</button>
                <button className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${colorFilter === "Yellow" ? "bg-yellow-400 text-yellow-900" : "bg-yellow-100 text-yellow-700 hover:bg-yellow-200"}`} onClick={() => setColorFilter(colorFilter === "Yellow" ? null : "Yellow")} data-testid="filter-yellow">Yellow</button>
                <button className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${colorFilter === "Red" ? "bg-red-500 text-white" : "bg-red-100 text-red-700 hover:bg-red-200"}`} onClick={() => setColorFilter(colorFilter === "Red" ? null : "Red")} data-testid="filter-red">Red</button>
                <button className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${colorFilter === "Blue" ? "bg-blue-500 text-white" : "bg-blue-100 text-blue-700 hover:bg-blue-200"}`} onClick={() => setColorFilter(colorFilter === "Blue" ? null : "Blue")} data-testid="filter-blue">Blue</button>
                <button className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${colorFilter === "Green" ? "bg-green-500 text-white" : "bg-green-100 text-green-700 hover:bg-green-200"}`} onClick={() => setColorFilter(colorFilter === "Green" ? null : "Green")} data-testid="filter-green">Green</button>
              </div>
            </div>

            {/* Camper Grid with Inline Editing */}
            {filteredCampers.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200" data-testid="empty-state">
                <Waves className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-800 mb-1">No Campers Found</h3>
                <p className="text-gray-500 text-sm">Try adjusting your search or filters.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="camper-grid">
                {filteredCampers
                  .sort((a, b) => (a.last_name || "").localeCompare(b.last_name || "") || (a.first_name || "").localeCompare(b.first_name || ""))
                  .map((camper) => (
                    <InlineCamperRow 
                      key={camper.id} 
                      camper={camper} 
                      lifeguard={selectedLifeguard}
                      onUpdate={handleCamperUpdate}
                      onDelete={(deletedId) => {
                        setCampers(prev => prev.filter(c => c.id !== deletedId));
                      }}
                      onClick={() => setSelectedCamper(camper)} 
                    />
                  ))}
              </div>
            )}
          </>
        )}
      </main>
      )}

      {/* Camper Detail Modal */}
      {selectedCamper && (
        <CamperDetailModal
          camper={selectedCamper}
          onClose={() => setSelectedCamper(null)}
          onUpdate={handleCamperUpdate}
          onDelete={(deletedId) => {
            // Remove deleted camper from state
            setCampers(prev => prev.filter(c => c.id !== deletedId));
            setSelectedCamper(null);
          }}
        />
      )}

      {/* Swimmer Report Modal */}
      {showReportModal && (
        <SwimmerReportModal onClose={() => setShowReportModal(false)} />
      )}

      {/* End of Summer Reports Modal */}
      {showEndOfSummerModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowEndOfSummerModal(false)}>
          <div 
            className="bg-white rounded-xl max-w-lg w-full p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="end-of-summer-modal"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-emerald-500/20 rounded-full">
                <Mail className="h-6 w-6 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">End of Summer Reports</h2>
                <p className="text-sm text-slate-400">Send swim reports to all parents</p>
              </div>
            </div>

            {endOfSummerPreview ? (
              <div className="space-y-4">
                <div className="bg-slate-900/50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Total Campers:</span>
                    <span className="text-white font-semibold">{endOfSummerPreview.total_campers}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">With Parent Email:</span>
                    <span className="text-emerald-400 font-semibold">{endOfSummerPreview.campers_with_parent_email}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Without Parent Email:</span>
                    <span className="text-amber-400 font-semibold">{endOfSummerPreview.campers_without_parent_email}</span>
                  </div>
                  <div className="border-t border-slate-700 pt-3 flex justify-between items-center">
                    <span className="text-slate-300 font-medium">Reports to Send:</span>
                    <span className="text-xl text-emerald-400 font-bold">{endOfSummerPreview.reports_to_send}</span>
                  </div>
                </div>

                {endOfSummerPreview.reports_to_send === 0 ? (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-amber-300 text-sm">
                    <strong>No parent emails configured.</strong> Add parent emails to camper records before sending reports.
                  </div>
                ) : (
                  <>
                    {/* Schedule Status */}
                    {scheduleStatus && (
                      <div className="bg-slate-900/50 rounded-lg p-4 space-y-2">
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`w-2 h-2 rounded-full ${scheduleStatus.is_scheduled ? 'bg-emerald-400' : 'bg-slate-500'}`}></div>
                          <span className="text-sm font-medium text-slate-300">
                            {scheduleStatus.is_scheduled ? 'Auto-Send Scheduled' : 'Auto-Send Disabled'}
                          </span>
                        </div>
                        {scheduleStatus.is_scheduled && scheduleStatus.next_run_display && (
                          <div className="text-sm text-slate-400">
                            <strong className="text-emerald-400">Scheduled:</strong> {scheduleStatus.next_run_display}
                          </div>
                        )}
                        {scheduleStatus.last_run && (
                          <div className="text-sm text-slate-500">
                            Last run: {new Date(scheduleStatus.last_run).toLocaleString('en-US', { timeZone: 'America/New_York' })}
                            {scheduleStatus.last_status && ` (${scheduleStatus.last_status})`}
                          </div>
                        )}
                        {!scheduleStatus.webhook_configured && (
                          <div className="text-xs text-amber-400">
                            ⚠️ Webhook not configured - auto-send will not work
                          </div>
                        )}
                      </div>
                    )}

                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <p className="text-sm text-slate-400 mb-2">Sample Recipients:</p>
                      <div className="space-y-2">
                        {endOfSummerPreview.sample_recipients?.map((camper, i) => (
                          <div key={i} className="text-sm">
                            <span className="text-white">{camper.first_name} {camper.last_name}</span>
                            <span className="text-slate-500 ml-2">→</span>
                            <span className="text-emerald-400 ml-2">{camper.parent_email}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-blue-300 text-sm">
                      <strong>What gets sent:</strong> Each parent receives a PDF with their camper's complete swim assessment, including all color levels passed, instructor signatures, and the camp disclaimer.
                    </div>
                  </>
                )}

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                    onClick={() => setShowEndOfSummerModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white"
                    onClick={handleSendAllReports}
                    disabled={sendingReports || endOfSummerPreview.reports_to_send === 0}
                    data-testid="send-all-reports-btn"
                  >
                    {sendingReports ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send {endOfSummerPreview.reports_to_send} Reports
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 text-slate-500 animate-spin" />
                <span className="ml-3 text-slate-400">Loading preview...</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Daily Changes Modal */}
      {showDailyChangesModal && (
        <DailyChangesModal onClose={() => setShowDailyChangesModal(false)} />
      )}
    </div>
  );
}
